import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import FormChequeEmitido from "./FormChequeEmitido";
import SeleccionChequesRecibidos from "./SeleccionChequesRecibidos";
import {
  abrevOrigenCheque,
  colorOrigenCheque,
  labelOrigenCheque,
} from "../cheques_proveedores/cheques.constants";
import {
  chequeRecibidoAFila,
  chequesAsignadosOtrosProveedor,
  configPagoProveedorVacia,
  formatCuit,
  formatMoneda,
  idsChequesCarteraCargados,
  mediosResumenProveedorPreview,
  restanteMediosProveedor,
} from "./utils";

const soloDigitos = (valor) => String(valor || "").replace(/\D/g, "");

const formatearFecha = (valor) => {
  const fecha = String(valor || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return "—";
  const [anio, mes, dia] = fecha.split("-");
  return `${dia}/${mes}/${anio}`;
};

const TablaCheques = ({ filas, onQuitar }) => (
  <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: 1, mb: 1 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          {["Origen", "N° cheque", "Banco", "Importe", "Fecha pago", ""].map(
            (header, i) => (
              <TableCell
                key={`${header}-${i}`}
                sx={{ fontWeight: 600, bgcolor: "#f5f5f5" }}
              >
                {header}
              </TableCell>
            )
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {filas.map((fila) => (
          <TableRow key={`${fila.origen}-${fila.id ?? fila.key}`}>
            <TableCell>
              <Chip
                size="small"
                variant="outlined"
                color={colorOrigenCheque(fila)}
                label={abrevOrigenCheque(fila)}
                title={labelOrigenCheque(fila)}
              />
            </TableCell>
            <TableCell>{fila.nro_cheque || "—"}</TableCell>
            <TableCell>
              <Typography
                variant="body2"
                noWrap
                title={fila.banco_emisor}
                sx={{ maxWidth: 170 }}
              >
                {fila.banco_emisor || "—"}
              </Typography>
            </TableCell>
            <TableCell>{formatMoneda(fila.importe)}</TableCell>
            <TableCell>{formatearFecha(fila.fecha_pago)}</TableCell>
            <TableCell align="right">
              <Tooltip title="Quitar">
                <IconButton size="small" onClick={() => onQuitar(fila)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ResumenProveedor = ({ total, suma, restante }) => (
  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
    <Typography variant="body2">
      Total seleccionado: <strong>{formatMoneda(suma)}</strong>
    </Typography>
    <Typography
      variant="body2"
      color={
        restante > 0.02
          ? "warning.main"
          : restante < -0.02
            ? "error.main"
            : "success.main"
      }
    >
      {restante > 0.02
        ? "Faltante: "
        : restante < -0.02
          ? "Excedente: "
          : "Cubierto: "}
      <strong>{formatMoneda(Math.abs(restante) < 0.02 ? 0 : Math.abs(restante))}</strong>
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
      Total a pagar: {formatMoneda(total)}
    </Typography>
  </Stack>
);

function SeccionChequesProveedor({
  proveedor,
  recibidos,
  emitidos,
  idsExcluidosGlobal,
  chequesAsignadosOtros,
  restante,
  configPago,
  onChangeRecibidos,
  onChangeEmitidos,
  cuentasBancarias,
  loadingCuentas,
}) {
  const cuitProv = soloDigitos(proveedor?.cuit);
  const [dialogCartera, setDialogCartera] = useState(false);
  const [dialogEmitido, setDialogEmitido] = useState(false);

  const filas = useMemo(
    () => [
      ...recibidos.map((item) => ({ ...item, origen: "recibido" })),
      ...emitidos.map((item) => ({ ...item, origen: "emitido" })),
    ],
    [recibidos, emitidos]
  );

  const suma = filas.reduce(
    (acc, fila) => acc + (Number(String(fila.importe).replace(",", ".")) || 0),
    0
  );

  const totalProv = Number(proveedor?.total) || 0;
  const seExcede = totalProv > 0 && suma > totalProv + 0.02;

  const agregarRecibidos = (seleccionados) => {
    const nuevos = seleccionados.map((item) =>
      chequeRecibidoAFila(item, cuitProv)
    );
    onChangeRecibidos([...recibidos, ...nuevos]);
  };

  const agregarEmitido = (nuevo) => {
    onChangeEmitidos([...emitidos, { ...nuevo, cuit_asignado: cuitProv }]);
  };

  const quitarFila = (fila) => {
    if (fila.origen === "recibido") {
      onChangeRecibidos(
        recibidos.filter((item) => String(item.id) !== String(fila.id))
      );
      return;
    }
    onChangeEmitidos(emitidos.filter((item) => item.key !== fila.key));
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mb: 1.5, bgcolor: "#fafbfc", borderColor: "#d0dbe8" }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {proveedor?.razon_social || formatCuit(cuitProv)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {formatCuit(cuitProv)}
        {configPago?.medio ? ` · Propuesta: ${configPago.medio}` : ""}
      </Typography>

      <ResumenProveedor total={totalProv} suma={suma} restante={restante} />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AccountBalanceWalletIcon />}
          onClick={() => setDialogCartera(true)}
        >
          Usar de cartera
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setDialogEmitido(true)}
        >
          Cargar emitido
        </Button>
      </Stack>

      {filas.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Seleccioná cheques de la cartera para este proveedor.
        </Typography>
      ) : (
        <TablaCheques filas={filas} onQuitar={quitarFila} />
      )}

      {seExcede ? (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          Los cheques suman {formatMoneda(suma)} y superan el total a pagar de
          este proveedor ({formatMoneda(totalProv)}).
        </Alert>
      ) : null}

      <SeleccionChequesRecibidos
        open={dialogCartera}
        onClose={() => setDialogCartera(false)}
        onConfirmar={agregarRecibidos}
        idsExcluidos={idsExcluidosGlobal}
        chequesAsignadosOtros={chequesAsignadosOtros}
        restante={Math.max(restante, 0)}
        proveedorLabel={proveedor?.razon_social || formatCuit(cuitProv)}
      />

      <FormChequeEmitido
        open={dialogEmitido}
        onClose={() => setDialogEmitido(false)}
        onAgregar={agregarEmitido}
        restante={Math.max(restante, 0)}
        cuentasBancarias={cuentasBancarias}
        loadingCuentas={loadingCuentas}
      />
    </Paper>
  );
}

export default function PanelChequeMedio({
  cheque,
  onChange,
  restante = 0,
  totalAPagar = 0,
  proveedores = [],
  configPagoPorProveedor = {},
  medioPago = null,
  chequesAsignadosLote = null,
  previewMediosLote = null,
  cuentasBancarias = [],
  loadingCuentas = false,
}) {
  const [dialogCartera, setDialogCartera] = useState(false);
  const [dialogEmitido, setDialogEmitido] = useState(false);

  const recibidos = useMemo(() => cheque?.recibidos ?? [], [cheque?.recibidos]);
  const emitidos = useMemo(() => cheque?.emitidos ?? [], [cheque?.emitidos]);

  const multiProveedor = proveedores.length > 1;
  const cuitPorDefecto = proveedores.length === 1 ? soloDigitos(proveedores[0]?.cuit) : "";

  const idsExcluidosGlobal = useMemo(
    () =>
      medioPago
        ? idsChequesCarteraCargados(medioPago)
        : recibidos.map((item) => item.id).filter((id) => id != null).map(String),
    [medioPago, recibidos]
  );

  const chequesAsignadosPorId = useMemo(() => {
    const map = new Map();
    recibidos.forEach((item) => {
      if (item.id == null) return;
      const cuitAsig = soloDigitos(item.cuit_asignado);
      const prov = proveedores.find((p) => soloDigitos(p.cuit) === cuitAsig);
      map.set(String(item.id), {
        ...item,
        razon_social: prov?.razon_social || formatCuit(cuitAsig),
      });
    });
    return map;
  }, [recibidos, proveedores]);

  const filas = useMemo(
    () => [
      ...recibidos.map((item) => ({ ...item, origen: "recibido" })),
      ...emitidos.map((item) => ({ ...item, origen: "emitido" })),
    ],
    [recibidos, emitidos]
  );

  const subtotal = filas.reduce(
    (acc, fila) => acc + (Number(String(fila.importe).replace(",", ".")) || 0),
    0
  );

  const totalNum = Number(totalAPagar) || 0;
  const seExcedeTotal = !multiProveedor && totalNum > 0 && subtotal > totalNum + 0.02;

  const actualizarRecibidos = (nuevosRecibidos) => {
    onChange({ recibidos: nuevosRecibidos });
  };

  const actualizarEmitidos = (nuevosEmitidos) => {
    onChange({ emitidos: nuevosEmitidos });
  };

  const agregarRecibidos = (seleccionados) => {
    const nuevos = seleccionados.map((item) =>
      chequeRecibidoAFila(item, cuitPorDefecto)
    );
    onChange({ recibidos: [...recibidos, ...nuevos] });
  };

  const agregarEmitido = (nuevo) => {
    onChange({
      emitidos: [...emitidos, { ...nuevo, cuit_asignado: cuitPorDefecto }],
    });
  };

  const quitarFila = (fila) => {
    if (fila.origen === "recibido") {
      onChange({
        recibidos: recibidos.filter((item) => String(item.id) !== String(fila.id)),
      });
      return;
    }
    onChange({
      emitidos: emitidos.filter((item) => item.key !== fila.key),
    });
  };

  if (multiProveedor) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Cada proveedor tiene su propia selección de cheques. Un cheque asignado
          a un proveedor no puede reutilizarse en otro.
        </Typography>

        {proveedores.map((prov) => {
          const cuitProv = soloDigitos(prov.cuit);
          const cfg =
            configPagoPorProveedor?.[cuitProv] ??
            configPagoPorProveedor?.[prov.cuit] ??
            configPagoProveedorVacia();
          const recibidosProv = recibidos.filter(
            (item) => soloDigitos(item.cuit_asignado) === cuitProv
          );
          const emitidosProv = emitidos.filter(
            (item) => soloDigitos(item.cuit_asignado) === cuitProv
          );
          const resumenBack = mediosResumenProveedorPreview(previewMediosLote, cuitProv);
          const restanteProv = resumenBack
            ? Number(resumenBack.faltante) || 0
            : restanteMediosProveedor(
                medioPago || { cheque, efectivo: { activo: false } },
                cuitProv,
                prov.total,
                cfg
              );
          const chequesAsignadosOtrosLocal = recibidos
            .filter((item) => soloDigitos(item.cuit_asignado) !== cuitProv)
            .map((item) => ({
              id: item.id,
              nro_cheque: item.nro_cheque,
              importe: item.importe,
              banco_emisor: item.banco_emisor,
              razon_social:
                chequesAsignadosPorId.get(String(item.id))?.razon_social ||
                formatCuit(item.cuit_asignado),
            }));
          const chequesAsignadosOtros =
            chequesAsignadosLote && Object.keys(chequesAsignadosLote).length > 0
              ? chequesAsignadosOtrosProveedor(
                  chequesAsignadosLote,
                  cuitProv,
                  proveedores
                )
              : chequesAsignadosOtrosLocal;

          return (
            <SeccionChequesProveedor
              key={cuitProv}
              proveedor={prov}
              recibidos={recibidosProv}
              emitidos={emitidosProv}
              idsExcluidosGlobal={idsExcluidosGlobal}
              chequesAsignadosOtros={chequesAsignadosOtros}
              restante={restanteProv}
              configPago={cfg}
              onChangeRecibidos={(nuevosProv) => {
                const otros = recibidos.filter(
                  (item) => soloDigitos(item.cuit_asignado) !== cuitProv
                );
                actualizarRecibidos([...otros, ...nuevosProv]);
              }}
              onChangeEmitidos={(nuevosProv) => {
                const otros = emitidos.filter(
                  (item) => soloDigitos(item.cuit_asignado) !== cuitProv
                );
                actualizarEmitidos([...otros, ...nuevosProv]);
              }}
              cuentasBancarias={cuentasBancarias}
              loadingCuentas={loadingCuentas}
            />
          );
        })}
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AccountBalanceWalletIcon />}
          onClick={() => setDialogCartera(true)}
        >
          Usar de cartera
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setDialogEmitido(true)}
        >
          Cargar emitido
        </Button>
      </Stack>

      {filas.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Elegí cheques disponibles de la cartera o cargá uno emitido para esta
          orden de pago.
        </Typography>
      ) : (
        <>
          <TablaCheques filas={filas} onQuitar={quitarFila} />
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2">
              {filas.length} cheque{filas.length === 1 ? "" : "s"} ·{" "}
              <strong>{formatMoneda(subtotal)}</strong>
            </Typography>
            {recibidos.length > 0 ? (
              <Typography variant="caption" color="text.secondary">
                {recibidos.length} de cartera (se endosan al confirmar)
              </Typography>
            ) : null}
          </Stack>
        </>
      )}

      {seExcedeTotal ? (
        <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
          Los cheques suman {formatMoneda(subtotal)} y superan el total a pagar
          ({formatMoneda(totalNum)}).
        </Alert>
      ) : null}

      <SeleccionChequesRecibidos
        open={dialogCartera}
        onClose={() => setDialogCartera(false)}
        onConfirmar={agregarRecibidos}
        idsExcluidos={idsExcluidosGlobal}
        restante={restante}
      />

      <FormChequeEmitido
        open={dialogEmitido}
        onClose={() => setDialogEmitido(false)}
        onAgregar={agregarEmitido}
        restante={restante}
        cuentasBancarias={cuentasBancarias}
        loadingCuentas={loadingCuentas}
      />
    </Box>
  );
}
